import { useEffect, useRef, useState } from 'react'

// VERA-837: WorkOS scores passwords with zxcvbn (entropy estimate) + a breach
// check server-side, not a rules checklist — an 8-char password like
// "Testing1" passes our old client-side "8 characters" rule and is then
// rejected by WorkOS with "Password does not meet strength requirements."
// This hook mirrors that scoring client-side so the user gets feedback
// before they submit, not after.
//
// Threshold: WorkOS does not document its exact zxcvbn cutoff anywhere
// discoverable in this repo (grepped apps/api for `zxcvbn`/`password_strength`
// and the @workos-inc/node package — neither exposes it; WorkOS's server-side
// check is opaque from the client). Defaulted to the common WorkOS/industry
// cutoff of zxcvbn score >= 3 (of 0-4), then EMPIRICALLY CONFIRMED against
// the real running API (POST /auth/password/setup) on 2026-08-01:
//   score 1 "Testing1"                       -> 400 "does not meet strength requirements"
//   score 2 "greenhouse2026"                 -> 400 "does not meet strength requirements"
//   score 3 "Purplemoon48" (boundary case)   -> 200 "Password set successfully"
//   score 4 "CorrelateHazyMuffinTractor92!"  -> 200 "Password set successfully"
// i.e. WorkOS's real cutoff lands exactly at score 3 for these probes — not
// just "conservative enough," but tight. The one asymmetric failure mode this
// can't rule out is WorkOS being STRICTER than score 3 for some password
// shapes (e.g. its breach check, which zxcvbn has no visibility into) — the
// server error path is deliberately left fully intact (see
// ForgotPassword/SignInBanner/EditFieldModal) so that case still surfaces a
// real, actionable message rather than a false "Strong."
export const PASSWORD_STRENGTH_THRESHOLD = 3

export type PasswordStrengthLabel = 'Weak' | 'Fair' | 'Good' | 'Strong'

const LABELS: PasswordStrengthLabel[] = ['Weak', 'Weak', 'Fair', 'Good', 'Strong']

export interface PasswordStrengthResult {
  /** 0-4 (zxcvbn's own scale), or null while the password is empty / the scorer hasn't loaded yet. */
  score: number | null
  label: PasswordStrengthLabel | null
  /** zxcvbn's own actionable guidance, e.g. "Add another word or two" */
  suggestion: string | null
  warning: string | null
  /** true once score !== null && score >= PASSWORD_STRENGTH_THRESHOLD */
  meetsThreshold: boolean
  /** true while the zxcvbn chunk is being lazy-loaded (first keystroke only) */
  isLoading: boolean
}

// Module-level singleton so the ~lazy chunk + dictionary setup only happens
// once per session, not once per mounted field (Profile, SignIn, ForgotPassword
// all use this hook).
let zxcvbnInstancePromise: Promise<{ check: (password: string) => { score: number; feedback: { warning: string; suggestions: string[] } } }> | null = null

const getZxcvbn = () => {
  if (!zxcvbnInstancePromise) {
    // Dynamic import — keeps @zxcvbn-ts/core + language-common (dictionaries +
    // adjacency graphs) out of the initial bundle. Only fetched on first use.
    zxcvbnInstancePromise = Promise.all([
      import('@zxcvbn-ts/core'),
      import('@zxcvbn-ts/language-common'),
      // Only the translations subpath (~2.5KB) — NOT the full
      // '@zxcvbn-ts/language-en' package, which also bundles English
      // first/last-name + Wikipedia wordlists (~1.2MB unminified / ~600KB
      // gzipped). @zxcvbn-ts/language-common's diceware/passwords-common
      // dictionaries are already sufficient for scoring; we only need
      // language-en for its human-readable feedback strings (without this,
      // feedback.warning/suggestions are raw translation keys like "common").
      import('@zxcvbn-ts/language-en/dist/translations.mjs'),
    ]).then(([core, common, en]) => {
      const options = {
        dictionary: {
          ...common.dictionary,
        },
        graphs: common.adjacencyGraphs,
        // translations.mjs is `export { translations as default }` — the
        // module's default export IS the translations object.
        translations: en.default,
      }
      return new core.ZxcvbnFactory(options)
    })
  }
  return zxcvbnInstancePromise
}

/**
 * Live client-side password-strength scoring (zxcvbn-style), matching the
 * scoring model WorkOS uses server-side. See PASSWORD_STRENGTH_THRESHOLD for
 * the cutoff + its provenance.
 */
export const usePasswordStrength = (password: string): PasswordStrengthResult => {
  const [result, setResult] = useState<PasswordStrengthResult>({
    score: null,
    label: null,
    suggestion: null,
    warning: null,
    meetsThreshold: false,
    isLoading: false,
  })

  // Guards against a stale async response overwriting a newer one (fast typing).
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!password) {
      setResult({ score: null, label: null, suggestion: null, warning: null, meetsThreshold: false, isLoading: false })
      return
    }

    const requestId = ++requestIdRef.current
    setResult(prev => ({ ...prev, isLoading: true }))

    // Debounce isn't needed beyond a microtask — zxcvbn's `check` is
    // synchronous once loaded, and the dynamic import naturally coalesces
    // (the promise is cached), so this doesn't block typing.
    getZxcvbn().then(zxcvbn => {
      if (requestId !== requestIdRef.current) return // stale
      const { score, feedback } = zxcvbn.check(password)
      setResult({
        score,
        label: LABELS[score] ?? null,
        suggestion: feedback.suggestions?.[0] ?? null,
        warning: feedback.warning || null,
        meetsThreshold: score >= PASSWORD_STRENGTH_THRESHOLD,
        isLoading: false,
      })
    })
  }, [password])

  return result
}
